<template>
  <div>
    <Loading :isActive="carregando" />

    <CBreadcrumb :items="caminho" add-classes="breadcrumb-integracao" />

    <!--
      O erro de carga mora fora do bloco do conector.

      Dentro dele, uma falha ao buscar o conector — slug inexistente, 500,
      queda de rede — não renderizava nada: `conector` continua nulo, o alerta
      ficava junto com o resto e o usuário terminava com o caminho de migalhas
      e uma tela vazia, sem saber se ainda carregava.
    -->
    <CAlert v-if="erroGeral && !conector" show color="danger">{{ erroGeral }}</CAlert>

    <EmptyState
      v-if="!conector && !carregando"
      icon="plug"
      title="Não foi possível abrir esta integração"
      :description="erroGeral || 'O conector não foi encontrado ou não está mais disponível.'"
    >
      <template #action>
        <CButton color="primary" @click="voltar">Voltar para integrações</CButton>
      </template>
    </EmptyState>

    <template v-if="conector">
      <CTabs variant="tabs" class="nav-tabs-boxed">
      <CTab :title="conector.nome">

      <div class="cabecalho">
        <div class="cabecalho__identificacao">
          <LogoIntegracao
            :logo="conector.logo"
            :nome="conector.nome"
            :icone="conector.icone"
            tamanho="medio"
          />
          <div>
            <h4 class="cabecalho__nome">
              {{ conector.nome }}
              <span
                v-if="conector.parceria_rotulo"
                class="selo-parceiro"
                :title="'Integração homologada junto com ' + (conector.parceiro_nome || conector.nome)"
              >
                <fa-icon icon="award" /> {{ conector.parceria_rotulo }}
                <template v-if="conector.parceiro_nome"> · {{ conector.parceiro_nome }}</template>
              </span>
              <span v-if="conector.situacao === 'BETA'" class="selo-beta">Beta</span>
            </h4>
            <p class="cabecalho__descricao">
              {{ conector.descricao_curta }}
              <a
                v-if="siteDoParceiro"
                class="cabecalho__site"
                :href="siteDoParceiro"
                target="_blank"
                rel="noopener"
              >
                <fa-icon icon="globe" /> Site do parceiro
              </a>
            </p>
          </div>
        </div>

        <CButton variant="outline" color="secondary" @click="voltar">
          <fa-icon icon="angle-double-left" /> Voltar
        </CButton>
      </div>

      <!--
        Trocar de finalidade sem voltar à lista. Nos oito servidores de e-mail,
        conferir um por um significava oito idas e voltas; aqui são oito abas.
      -->
      <div v-if="finalidades.length > 1" class="finalidades">
        <button
          v-for="finalidade in finalidades"
          :key="finalidade.chave"
          type="button"
          class="finalidades__item"
          :class="{
            'finalidades__item--atual': finalidade.atual,
            'finalidades__item--vazia': !finalidade.id,
          }"
          :title="finalidade.id ? 'Abrir esta finalidade' : 'Ainda sem servidor configurado'"
          @click="irParaFinalidade(finalidade)"
        >
          {{ finalidade.nome }}
          <span v-if="!finalidade.id" class="finalidades__pendente">sem configuração</span>
        </button>
      </div>

      <CAlert v-if="erroGeral" show color="danger">{{ erroGeral }}</CAlert>

      <CAlert
        v-if="resultadoTeste"
        show
        :color="resultadoTeste.sucesso ? 'success' : 'danger'"
      >
        {{ resultadoTeste.mensagem }}
      </CAlert>

      <!--
        Sem a licença do módulo, a tela abre mesmo assim: é daqui que o cliente
        descobre o que o recurso faz. O que muda é o rodapé, que no lugar de
        salvar explica o que falta.
      -->
      <div class="layout" :class="{ 'layout--unico': modoAssistente }">
        <div class="coluna-principal">
          <!--
            O que o conector faz e como fica na tela. Vem antes do formulário
            para quem ainda não tem a licença, porque é a resposta à pergunta
            que o trouxe aqui; para quem já tem, são só as capturas.
          -->
          <ApresentacaoConector :conector="conector" />

          <!--
            Sem licença, o aviso tem de aparecer onde o usuário está. Ele só
            existia no rodapé, que o guia não desenha — quem instalava pelo
            passo a passo percorria tudo e só descobria no fim, se descobrisse.
          -->
          <CAlert
            v-if="modoAssistente && !licenciado"
            show
            color="warning"
            class="aviso-sem-licenca"
          >
            <fa-icon icon="lock" />
            Esta configuração depende do módulo
            <strong>{{ conector.licenca_nome || conector.nome }}</strong>, que esta
            instalação ainda não tem. Você pode percorrer o guia para conhecer o
            recurso, mas a gravação fica bloqueada até a licença ser liberada.
          </CAlert>

          <!--
            Configuração guiada: uma pergunta por vez, com a explicação junto do
            campo que ela explica. É o padrão de quem está instalando; quem já
            configurou abre direto a tela completa, que é melhor para mexer num
            campo só.
          -->
          <AssistenteIntegracao
            v-if="modoAssistente && conector.slug"
            v-model="formulario.valores"
            :provedor="provedorSelecionado"
            :passos="passosDoTutorial"
            :erros="errosCampos"
            :url-webhook="urlWebhook"
            :eventos-webhook="eventosWebhook"
            :integracao-id="integracao && integracao.id"
            :conexao="conexaoHub"
            :salvando="salvando"
            :tem-mapeamento="!!(edicao && integracao.mapeamento_campos)"
            :tem-regras="conectorRoteavel"
            :tem-permissoes="conectorComPermissoes"
            :provedores="provedores"
            :conectar-ao-abrir="conectarAoAbrir"
            :slug="conector.slug"
            @escolher-provedor="selecionarProvedor"
            @desconectar="desconectarDoHub"
            @salvar-e-conectar="salvarEConectar"
            @retomada-cumprida="retomadaCumprida"
            @concluir="salvar"
            @ver-tudo="escolherModo('completo')"
          >
            <template #regras>
              <RegraRoteamento
                v-if="conector.slug"
                :slug="conector.slug"
                :configuracoes="formulario.situacoes"
                :prioridade="formulario.prioridade"
                :embutido="true"
                @update:configuracoes="formulario.situacoes = $event"
                @update:prioridade="formulario.prioridade = $event"
              />
            </template>

            <template #permissoes>
              <PermissoesUso
                v-if="conector.slug"
                :slug="conector.slug"
                :embutido="true"
              />
            </template>

            <template #mapeamento>
              <MapeamentoCampos
                v-if="edicao && integracao.mapeamento_campos"
                :integracao-id="integracao.id"
                :tipo="integracao.mapeamento_campos"
                :permite-busca="!!integracao.permite_campos_remotos"
                :campos-remotos-total="integracao.campos_remotos_total || 0"
                :campos-remotos-em="integracao.campos_remotos_em || ''"
                :embutido="true"
                @campos-buscados="carregar"
              />
            </template>
          </AssistenteIntegracao>

          <template v-if="!modoAssistente">
          <CCard>
            <CCardHeader>Identificação</CCardHeader>
            <CCardBody>
              <div v-if="exigeEscopo" class="form-group">
                <label for="integracao-escopo">
                  Finalidade <span class="obrigatorio">*</span>
                </label>
                <v-select
                  id="integracao-escopo"
                  :options="opcoesDeEscopo"
                  label="rotulo"
                  :value="escopoEscolhido"
                  :disabled="edicao"
                  placeholder="Selecione..."
                  @input="aoEscolherEscopo"
                />
                <small v-if="descricaoEscopoSelecionado" class="campo-ajuda">
                  {{ descricaoEscopoSelecionado }}
                </small>
              </div>

              <div class="form-group">
                <label for="integracao-nome">Nome</label>
                <input
                  id="integracao-nome"
                  v-model="formulario.nome"
                  class="form-control"
                  :placeholder="conector.nome"
                />
                <small class="campo-ajuda">
                  Como esta configuração aparece na lista. Em branco, usa o nome do conector.
                </small>
              </div>
            </CCardBody>
          </CCard>

          <CCard v-if="provedores.length > 1">
            <CCardHeader>Provedor</CCardHeader>
            <CCardBody>
              <div class="provedores">
                <button
                  v-for="opcao in provedores"
                  :key="opcao.chave"
                  type="button"
                  class="provedor-card"
                  :class="{ 'provedor-card--ativo': formulario.provedor === opcao.chave }"
                  @click="selecionarProvedor(opcao.chave)"
                >
                  <span class="provedor-card__nome">{{ opcao.nome }}</span>
                  <span v-if="opcao.descricao" class="provedor-card__descricao">
                    {{ opcao.descricao }}
                  </span>
                </button>
              </div>
            </CCardBody>
          </CCard>

          <PermissoesUso v-if="conector.slug" :slug="conector.slug" />

          <CCard v-if="provedorSelecionado">
            <CCardHeader>
              Configuração
              <small v-if="provedores.length > 1" class="text-muted">
                — {{ provedorSelecionado.nome }}
              </small>
            </CCardHeader>
            <CCardBody>
              <FormularioManifesto
                v-model="formulario.valores"
                :provedor="provedorSelecionado"
                :erros="errosCampos"
                :url-webhook="urlWebhook"
                :eventos-webhook="eventosWebhook"
                :integracao-id="integracao && integracao.id"
                :conexao="conexaoHub"
                :conectar-ao-abrir="conectarAoAbrir"
                :slug="conector.slug"
                :salvando="salvando"
                @desconectar="desconectarDoHub"
                @salvar-e-conectar="salvarEConectar"
                @retomada-cumprida="retomadaCumprida"
              />
            </CCardBody>
          </CCard>
          <!--
            O mapeamento e o que já chegou do serviço ficam ao lado da
            configuração que os produz, e não em dois outros módulos.
          -->
          <MapeamentoCampos
            v-if="edicao && integracao.mapeamento_campos"
            ref="mapeamento"
            :integracao-id="integracao.id"
            :tipo="integracao.mapeamento_campos"
            :permite-busca="!!integracao.permite_campos_remotos"
            :campos-remotos-total="integracao.campos_remotos_total || 0"
            :campos-remotos-em="integracao.campos_remotos_em || ''"
            @campos-buscados="carregar"
          />

          <EventosRecebidos
            v-if="edicao && integracao.log_tipo"
            :integracao-id="integracao.id"
            :tipo-log="integracao.log_tipo"
          />

          <RegraRoteamento
            v-if="conector.slug"
            :slug="conector.slug"
            :configuracoes="formulario.situacoes"
            :prioridade="formulario.prioridade"
            @update:configuracoes="formulario.situacoes = $event"
            @update:prioridade="formulario.prioridade = $event"
          />

          <ReplicarConfiguracao
            v-if="podeReplicar"
            ref="replicar"
            :integracao-id="integracao.id"
            :escopos="escoposDoManifesto"
            :escopo-atual="formulario.escopo"
            :instancias="integracoesDoConector"
            @replicada="carregar"
          />

          <button type="button" class="alternar-modo" @click="escolherModo('assistente')">
            Configurar passo a passo
          </button>
          </template>
        </div>

        <aside v-if="!modoAssistente" class="coluna-lateral">
          <CCard>
            <CCardHeader>Situação</CCardHeader>
            <CCardBody>
              <div class="form-group">
                <label for="integracao-ativo">Integração ativa</label>
                <v-select
                  id="integracao-ativo"
                  :options="opcoesDeAtivo"
                  label="rotulo"
                  :clearable="false"
                  :value="opcoesDeAtivo.find((opcao) => opcao.valor === formulario.ativo)"
                  @input="formulario.ativo = $event ? $event.valor : false"
                />
                <small class="campo-ajuda">
                  Desativar mantém a configuração salva, mas o sistema volta ao
                  comportamento anterior à integração.
                </small>
              </div>

              <template v-if="edicao">
                <div class="situacao-linha">
                  <span class="rotulo">Estado</span>
                  <ChipStatusIntegracao
                    :ativo="!!integracao.ativo"
                    :status-conexao="integracao.status_conexao"
                  />
                </div>

                <div class="situacao-linha">
                  <span class="rotulo">Última verificação</span>
                  <span class="valor">{{ ultimaVerificacao }}</span>
                </div>

                <div v-if="integracao.ultimo_erro" class="situacao-erro">
                  {{ integracao.ultimo_erro }}
                </div>

                <CButton
                  v-if="integracao.permite_teste"
                  variant="outline"
                  color="info"
                  class="botao-largo"
                  :disabled="salvando || testando"
                  @click="testar"
                >
                  <fa-icon icon="plug" /> {{ testando ? 'Testando...' : 'Testar conexão' }}
                </CButton>
                <p v-else class="sem-teste">
                  Este conector não oferece teste automático de conexão.
                </p>
              </template>
              <p v-else class="sem-teste">
                Salve a configuração para poder testar a conexão.
              </p>
            </CCardBody>
          </CCard>

          <!--
            Quem mexeu, e o histórico completo a um clique. Integração é
            configuração sensível e mexida por mais de uma pessoa: quando o
            frete para de cotar na segunda-feira, a primeira pergunta é o que
            mudou e quem mudou.
          -->
          <CCard v-if="edicao">
            <CCardHeader>Alterações</CCardHeader>
            <CCardBody>
              <div class="situacao-linha">
                <span class="rotulo">Criada por</span>
                <span class="valor">{{ autoria.criadoPor }}</span>
              </div>
              <div class="situacao-linha">
                <span class="rotulo">Em</span>
                <span class="valor">{{ autoria.criadoEm }}</span>
              </div>

              <div class="situacao-linha">
                <span class="rotulo">Última alteração</span>
                <span class="valor">{{ autoria.atualizadoPor }}</span>
              </div>
              <div class="situacao-linha">
                <span class="rotulo">Em</span>
                <span class="valor">{{ autoria.atualizadoEm }}</span>
              </div>

              <LogAlteracaoDados
                tabela="integracao"
                :chave="integracao.id"
                unified
                :field-labels="rotulosDoLog"
                :registro-label="conector.nome + ' · ' + (formulario.nome || conector.nome)"
              >
                <template #trigger="{ open }">
                  <CButton
                    variant="outline"
                    color="secondary"
                    class="botao-largo"
                    @click="open"
                  >
                    <fa-icon icon="clipboard-list" /> Ver histórico de alterações
                  </CButton>
                </template>
              </LogAlteracaoDados>
            </CCardBody>
          </CCard>

          <TutorialIntegracao
            :manifesto="conector.manifesto"
            :descricao="conector.descricao"
          />
        </aside>
      </div>

      <!--
        No guia não há este rodapé: a etapa final tem o próprio "Concluir", e
        dois botões de salvar na mesma tela só fazem duvidar de qual é o certo.
      -->
      <CCard v-if="!modoAssistente" class="rodape-acoes">
        <!--
          Sem licença não há o que salvar, e um botão desabilitado só faz o
          usuário tentar descobrir por quê. No lugar deles, o que falta.
        -->
        <CCardBody v-if="!licenciado" class="rodape-acoes__corpo rodape-acoes__corpo--sem-licenca">
          <fa-icon icon="lock" />
          <span>
            Esta configuração depende do módulo
            <strong>{{ conector.licenca_nome || conector.nome }}</strong>, que esta
            instalação ainda não tem. Já contratou? A liberação aparece assim que a
            licença for sincronizada.
          </span>
        </CCardBody>

        <CCardBody v-else class="rodape-acoes__corpo">
          <span class="rodape-acoes__aviso">
            {{ TEXTOS.VALE_DEPOIS_DE_SALVAR }}
          </span>
          <CButton variant="outline" color="secondary" :disabled="salvando" @click="voltar">
            Cancelar
          </CButton>
          <CButton color="primary" :disabled="salvando" @click="salvar">
            {{ salvando ? 'Salvando...' : 'Salvar' }}
          </CButton>
        </CCardBody>
      </CCard>

      </CTab>
      </CTabs>
    </template>
  </div>
</template>

<script>
import TEXTOS from '@/views/Integracoes/textos';
/*
 * Página de configuração de uma integração — instalação (rota por slug do
 * conector) e edição (rota por id da instância) usam a mesma tela.
 *
 * É página e não modal de propósito: conectores como o de IA generativa têm
 * treze campos, com blocos de instruções longas em textarea. Num modal isso
 * vira rolagem dentro de rolagem, e o rodapé de ações come o pouco espaço que
 * sobra.
 */
import { del, get, post, put } from '@/helpers/apiRequest';
import AssistenteIntegracao from './components/AssistenteIntegracao';
import FormularioManifesto from './components/FormularioManifesto';
import ChipStatusIntegracao from './components/ChipStatusIntegracao';
import EventosRecebidos from './components/EventosRecebidos';
import MapeamentoCampos from './components/MapeamentoCampos';
import PermissoesUso from './components/PermissoesUso';
import TutorialIntegracao from './components/TutorialIntegracao';
import LogAlteracaoDados from '@/components/Log/LogAlteracaoDados';
import LogoIntegracao from './components/LogoIntegracao';
import ApresentacaoConector from './components/ApresentacaoConector';
import RegraRoteamento from './components/RegraRoteamento';
import ReplicarConfiguracao from './components/ReplicarConfiguracao';
import EmptyState from '@/components/EmptyState';
import { urlSegura } from '@/helpers/urlSegura';

/*
 * Como o usuário prefere configurar: guiado ou com tudo à vista. Fica no
 * navegador, como as outras preferências de tela do painel — é gosto de quem
 * está usando, não dado da integração, e vale para todas elas.
 *
 * Em navegador que bloqueia o armazenamento a leitura estoura; sem a
 * preferência a tela decide sozinha, que é o comportamento de quem nunca
 * escolheu.
 */
const CHAVE_PREFERENCIA = 'integracoes_modo_configuracao';

function lerPreferencia() {
  try {
    return localStorage.getItem(CHAVE_PREFERENCIA) || '';
  } catch (erro) {
    return '';
  }
}

function gravarPreferencia(modo) {
  try {
    localStorage.setItem(CHAVE_PREFERENCIA, modo);
  } catch (erro) {
    // Preferência é conforto, não configuração: sem onde guardar, segue.
  }
}

/*
 * Telas de fora do módulo que mandam o usuário configurar algo aqui. O valor
 * chega em `?origem=` e diz para onde devolvê-lo depois.
 */
const TELAS_DE_ORIGEM = {
  'configuracao-ia': { texto: 'Configuração de IA', caminho: '/configuracoes/configuracao-ia' },
};

export default {
  name: 'ConfiguracaoIntegracao',
  components: {
    ApresentacaoConector,
    AssistenteIntegracao,
    EmptyState,
    ChipStatusIntegracao,
    EventosRecebidos,
    FormularioManifesto,
    MapeamentoCampos,
    LogoIntegracao,
    PermissoesUso,
    RegraRoteamento,
    ReplicarConfiguracao,
    TutorialIntegracao,
    LogAlteracaoDados,
  },
  data() {
    return {
      // Exposto para o template: as frases que outras telas do módulo repetem.
      TEXTOS,
      conector: null,
      integracao: null,
      integracoesDoConector: [],
      // Provedor escolhido no botão de conectar antes de a integração existir:
      // guarda o clique até o salvar devolver o identificador.
      conectarApos: '',
      // Leva a tela até o mapeamento assim que ele existir, uma vez só.
      irParaMapeamento: false,
      /*
       * Vazio deixa a tela decidir: guiada para quem está instalando, completa
       * para quem já configurou. Preenchido, é escolha do usuário — e ela vale
       * daí em diante, não só nesta visita: quem disse que prefere ver tudo
       * não quer dizê-lo de novo a cada integração que abrir.
       */
      modo: lerPreferencia(),
      formulario: {
        nome: '',
        escopo: '',
        provedor: '',
        valores: {},
        ativo: true,
        situacoes: {},
        prioridade: 100,
      },
      errosCampos: {},
      erroGeral: '',
      resultadoTeste: null,
      carregando: true,
      salvando: false,
      testando: false,
    };
  },
  computed: {
    edicao() {
      return !!this.$route.params.id;
    },

    /**
     * De qual aba o usuário veio. Sem indicação, a lista de configuradas — que
     * é a primeira e a que mais se usa.
     */
    abaDeOrigem() {
      return this.$route.query.origem === 'catalogo' ? 'catalogo' : 'configuradas';
    },

    /**
     * Para onde o botão de voltar e a trilha devolvem o usuário.
     *
     * Nem sempre é o módulo de Integrações: há telas de configuração que
     * mandam para cá o que é técnico e continuam donas do resto — a
     * Configuração de IA é o caso. Quem chegou de lá espera voltar para lá, e
     * não para uma lista que não estava procurando.
     */
    telaDeOrigem() {
      const externa = TELAS_DE_ORIGEM[this.$route.query.origem];

      if (externa) {
        return { text: externa.texto, to: { path: externa.caminho } };
      }

      return { text: 'Integrações', to: { path: '/integracoes', query: { aba: this.abaDeOrigem } } };
    },

    caminho() {
      return [
        this.telaDeOrigem,
        { text: this.conector ? this.conector.nome : 'Configuração' },
      ];
    },

    provedores() {
      return (this.conector && this.conector.manifesto && this.conector.manifesto.provedores) || [];
    },

    provedorSelecionado() {
      return this.provedores.find((p) => p.chave === this.formulario.provedor) || null;
    },

    /*
     * Só conector multi-instância com escopos declarados no manifesto pede
     * finalidade — é o caso do SMTP, um servidor por tipo de envio. Conector
     * multi-instância sem escopos (frete por filial) é nomeado livremente.
     */
    /*
     * Conector sem dependência de módulo vem liberado; a checagem contra
     * `false` evita travar a tela enquanto o conector ainda não carregou.
     */
    /* O endereço do parceiro vem do manifesto; só vira link se for http(s). */
    siteDoParceiro() {
      return urlSegura(this.conector && this.conector.site);
    },

    licenciado() {
      return !this.conector || this.conector.licenca_liberada !== false;
    },

    exigeEscopo() {
      return !!(this.conector && this.conector.multi_instancia && this.escoposDoManifesto.length);
    },

    /*
     * O endereço só existe depois que a instância foi salva — é do token dela
     * que ele nasce. Antes disso o campo explica isso ao usuário em vez de
     * mostrar uma URL inventada.
     */
    /*
     * Endereço fixo vem do conector e vale antes de salvar — é o mesmo para
     * todas as instâncias. O por token pertence à instância e só existe depois
     * que ela existe.
     */
    urlWebhook() {
      return (this.integracao && this.integracao.url_webhook)
        || (this.conector && this.conector.url_webhook_fixa)
        || '';
    },

    /*
     * Estado da conta conectada pelo Hub, quando o conector tem um campo desses.
     * Nulo antes de salvar: a conexão pertence à instância, e sem ela não há a
     * que amarrar a autorização.
     */
    conexaoHub() {
      return (this.integracao && this.integracao.conexao_hub) || {};
    },

    eventosWebhook() {
      return (this.integracao && this.integracao.eventos_webhook)
        || (this.conector && this.conector.manifesto && this.conector.manifesto.webhook
          && this.conector.manifesto.webhook.eventos)
        || [];
    },

    /*
     * Nomes e datas de quem mexeu, prontos para a tela. O travessão no lugar do
     * vazio é para as instâncias criadas pela própria migração, que não têm
     * autor — ninguém as configurou.
     */
    autoria() {
      const dados = (this.integracao && this.integracao.autoria) || {};

      return {
        criadoPor: dados.criado_por || '—',
        criadoEm: this.dataHora(dados.criado_em),
        atualizadoPor: dados.atualizado_por || '—',
        atualizadoEm: this.dataHora(dados.atualizado_em),
      };
    },

    /*
     * Nomes de campo como o usuário os vê no formulário. Sem isso o histórico
     * mostraria `catalogo_slug` e `chave_rastreio`, que são nomes de coluna.
     */
    rotulosDoLog() {
      return {
        nome: 'Nome',
        escopo: 'Finalidade',
        provedor: 'Provedor',
        configuracao: 'Configuração',
        credenciais: 'Credenciais',
        ativo: 'Integração ativa',
        situacoes: 'Regras por situação',
        prioridade: 'Prioridade',
        chave_rastreio: 'Rastrear pelo',
        filial_id: 'Filial',
        status_conexao: 'Estado da conexão',
        testado_em: 'Última verificação',
        ultimo_erro: 'Erro da última verificação',
        webhook_token: 'Endereço de webhook',
        atualizado_por: 'Alterado por',
        criado_por: 'Criado por',
      };
    },

    /*
     * Chave do provedor a conectar assim que a tela abrir. Chega pela query
     * quando a instalação foi iniciada pelo botão de conectar.
     */
    conectarAoAbrir() {
      /*
       * Voltando do provedor, quem manda é a conclusão: a sessão que chegou
       * precisa ser trocada antes de qualquer coisa. Começar outra aqui
       * mandaria o usuário de volta ao provedor no exato momento em que ele
       * acabou de voltar dele.
       */
      if (this.$route.query.hub_session || this.$route.query.state) {
        return '';
      }

      return this.$route.query.conectar || '';
    },

    /*
     * O guia é o caminho de quem instala. Quem já tem a integração no ar quase
     * sempre vem mexer num campo só, e percorrer cinco etapas para chegar nele
     * seria pior do que a tela cheia.
     */
    modoAssistente() {
      if (this.modo) {
        return this.modo === 'assistente';
      }

      return !this.edicao;
    },

    /*
     * As mesmas listas de antes, agora como opções de um campo com busca: a
     * tela de integração inteira usa o mesmo componente de escolha, e não um
     * select simples aqui e um com busca ali.
     */
    opcoesDeEscopo() {
      return this.escoposDisponiveis.map((escopo) => ({
        valor: escopo.chave,
        rotulo: escopo.nome + (escopo.ocupado ? ' (já configurada)' : ''),
        ocupado: escopo.ocupado,
      }));
    },

    escopoEscolhido() {
      return this.opcoesDeEscopo.find((opcao) => opcao.valor === this.formulario.escopo) || null;
    },

    opcoesDeAtivo() {
      return [
        { valor: true, rotulo: 'Sim' },
        { valor: false, rotulo: 'Não' },
      ];
    },

    passosDoTutorial() {
      return (this.conector && this.conector.manifesto
        && this.conector.manifesto.tutorial
        && this.conector.manifesto.tutorial.passos) || [];
    },

    escoposDoManifesto() {
      return (this.conector && this.conector.manifesto && this.conector.manifesto.escopos) || [];
    },

    /*
     * Só depois de salva: replicar copia o que está gravado, não o que está na
     * tela. Oferecer antes faria a cópia sair com a configuração anterior.
     */
    /*
     * Conector cujas configurações são escolhidas por regra — frete, WMS,
     * telefonia. É o que decide se o passo a passo oferece a etapa das regras:
     * num conector de instância única a pergunta não existe.
     */
    conectorRoteavel() {
      return !!(this.conector && this.conector.manifesto && this.conector.manifesto.roteavel);
    },

    /*
     * A pergunta "quem pode usar" só existe para o conector que declara
     * permissões de uso. Ela sai do manifesto, e não de uma consulta: a etapa
     * precisa existir antes de o bloco ser montado, e quem monta o bloco é a
     * própria etapa.
     */
    conectorComPermissoes() {
      const declaradas = this.conector
        && this.conector.manifesto
        && this.conector.manifesto.permissoes_uso;

      return !!(declaradas && declaradas.length);
    },

    podeReplicar() {
      return this.edicao && this.exigeEscopo && this.escoposDoManifesto.length > 1;
    },

    /*
     * As finalidades declaradas pelo conector, com a instância de cada uma
     * quando já existe. As que ainda não têm configuração aparecem marcadas:
     * é assim que se percebe que o e-mail de boleto ficou sem servidor.
     */
    finalidades() {
      if (!this.exigeEscopo) {
        return [];
      }

      return this.escoposDoManifesto.map((escopo) => {
        const instancia = this.integracoesDoConector.find(
          (integracao) => integracao.escopo === escopo.chave
        );

        return {
          chave: escopo.chave,
          nome: escopo.nome || escopo.chave,
          id: instancia ? instancia.id : null,
          atual: !!instancia && !!this.integracao && instancia.id === this.integracao.id,
        };
      });
    },

    escoposDisponiveis() {
      const ocupados = this.integracoesDoConector
        .filter((i) => !this.integracao || i.id !== this.integracao.id)
        .map((i) => i.escopo);

      return this.escoposDoManifesto.map((escopo) => ({
        ...escopo,
        ocupado: ocupados.includes(escopo.chave),
      }));
    },

    descricaoEscopoSelecionado() {
      const escopo = this.escoposDoManifesto.find((e) => e.chave === this.formulario.escopo);

      return escopo ? escopo.descricao : '';
    },

    ultimaVerificacao() {
      if (!this.integracao || !this.integracao.testado_em) {
        return TEXTOS.NUNCA_TESTADA;
      }

      return new Date(this.integracao.testado_em).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  methods: {
    carregar() {
      this.carregando = true;

      const requisicao = this.edicao
        ? this.carregarInstancia()
        : this.carregarConector(this.$route.params.slug);

      requisicao
        .catch((erro) => {
          this.erroGeral = erro.message;
        })
        .finally(() => {
          this.carregando = false;
          this.rolarParaReplicar();

          if (this.irParaMapeamento) {
            this.irParaMapeamento = false;
            this.mostrarMapeamento();
          }
        });
    },

    /*
     * Chegando pelo atalho "aplicar a outras", a tela abre no bloco de cópia —
     * quem clicou ali já sabe o que quer fazer e não precisa procurá-lo no fim
     * da página.
     */
    rolarParaReplicar() {
      if (this.$route.query.replicar !== '1') {
        return;
      }

      this.$nextTick(() => {
        const bloco = this.$refs.replicar && this.$refs.replicar.$el;

        if (bloco) {
          bloco.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    carregarInstancia() {
      return get(`/admin/integracoes/${this.$route.params.id}`).then((integracao) => {
        this.integracao = integracao;

        return this.carregarConector(integracao.catalogo_slug).then(() => {
          this.formulario = {
            nome: integracao.nome || '',
            escopo: integracao.escopo || '',
            provedor: integracao.provedor || '',
            valores: { ...(integracao.valores || {}) },
            ativo: !!integracao.ativo,
            situacoes: integracao.situacoes || {},
            prioridade: integracao.prioridade || 100,
          };
        });
      });
    },

    /**
     * Rebusca a instância sem tocar no formulário.
     *
     * `carregarInstancia()` reescreve `formulario` inteiro com o que está
     * gravado, e depois de testar a conexão isso apagava a credencial que o
     * usuário tinha acabado de digitar — justamente o gesto que leva ao botão.
     * O painel lateral lê só `integracao`, então é só ela que precisa voltar.
     */
    recarregarEstadoDaInstancia() {
      if (!this.integracao || !this.integracao.id) {
        return Promise.resolve();
      }

      return get(`/admin/integracoes/${this.integracao.id}`)
        .then((integracao) => {
          this.integracao = integracao;
        })
        .catch(() => {
          // O resultado do teste já está na tela; falhar a releitura do estado
          // não é motivo para sobrepor a resposta que o usuário veio buscar.
        });
    },

    carregarConector(slug) {
      return get(`/admin/integracoes/catalogo/${slug}`).then((conector) => {
        this.conector = conector;

        if (!this.edicao) {
          this.prepararNova();
        }

        // Instâncias já existentes do mesmo conector: é o que diz quais
        // finalidades já estão ocupadas e, no de instância única, se ainda
        // há o que instalar.
        return this.carregarInstanciasDoConector(slug).then(() => this.desviarSeJaInstalado());
      });
    },

    /*
     * Conector de instância única já instalado não tem o que instalar: a tela
     * leva à configuração que existe.
     *
     * O Marketplace já faz esse desvio, mas com a lista que carregou — e ela
     * envelhece numa aba aberta antes da instalação, num link guardado ou no
     * botão de voltar do navegador. Sem a trava aqui, o usuário preenche o
     * formulário inteiro para o salvar recusar no fim, dizendo que o conector
     * aceita só uma configuração.
     */
    desviarSeJaInstalado() {
      if (this.edicao || !this.conector || this.conector.multi_instancia) {
        return;
      }

      const existente = this.integracoesDoConector[0];

      if (!existente) {
        return;
      }

      // Substitui em vez de empilhar: voltar traria de novo a tela que
      // acabou de ser recusada.
      this.$router.replace({
        path: `/integracoes/${existente.id}/configurar`,
        query: this.$route.query,
      });
    },

    carregarInstanciasDoConector(slug) {
      return get('/admin/integracoes').then((integracoes) => {
        this.integracoesDoConector = integracoes.filter((i) => i.catalogo_slug === slug);
      });
    },

    prepararNova() {
      const provedorUnico = this.provedores.length === 1 ? this.provedores[0].chave : '';

      this.formulario = {
        nome: '',
        // Vindo das abas de finalidade, a escolha já foi feita lá.
        escopo: this.$route.query.escopo || '',
        provedor: provedorUnico,
        valores: this.valoresPadrao(provedorUnico),
        ativo: true,
        situacoes: {},
        prioridade: 100,
      };
    },

    dataHora(valor) {
      if (!valor) {
        return '—';
      }

      return new Date(valor).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    valoresPadrao(chaveProvedor) {
      const provedor = this.provedores.find((p) => p.chave === chaveProvedor);
      const valores = {};

      ((provedor && provedor.campos) || []).forEach((campo) => {
        if (campo.padrao !== undefined) {
          valores[campo.chave] = campo.padrao;
        }
      });

      return valores;
    },

    /*
     * Trocar de provedor troca o conjunto de campos, então os valores do
     * anterior são descartados: arrastá-los enviaria credencial de um serviço
     * para outro.
     */
    selecionarProvedor(chave) {
      if (this.formulario.provedor === chave) {
        return;
      }

      this.formulario.provedor = chave;
      this.formulario.valores = this.valoresPadrao(chave);
      this.errosCampos = {};
    },

    /*
     * Finalidade já configurada abre a sua própria tela; a que ainda não existe
     * leva à instalação com ela pré-selecionada, para não obrigar a voltar ao
     * catálogo e escolher de novo.
     */
    irParaFinalidade(finalidade) {
      if (finalidade.atual) {
        return;
      }

      if (finalidade.id) {
        this.$router.push(`/integracoes/${finalidade.id}/configurar`);

        return;
      }

      this.$router.push({
        path: `/integracoes/instalar/${this.conector.slug}`,
        query: { escopo: finalidade.chave },
      });
    },

    aoTrocarEscopo() {
      const escopo = this.escoposDoManifesto.find((e) => e.chave === this.formulario.escopo);

      if (escopo && !this.formulario.nome) {
        this.formulario.nome = `${this.conector.nome} — ${escopo.nome}`;
      }
    },

    /*
     * Finalidade já configurada não pode ser escolhida de novo — seria criar
     * uma segunda integração para o mesmo lugar, que o backend recusa.
     */
    aoEscolherEscopo(opcao) {
      if (opcao && opcao.ocupado) {
        return;
      }

      this.formulario.escopo = opcao ? opcao.valor : '';
      this.aoTrocarEscopo();
    },

    /**
     * Rola até o mapeamento e o destaca por um instante.
     *
     * Sem o destaque, a página só aparece rolada e o usuário não sabe por quê
     * — a ligação entre o que ele acabou de concluir e o que está vendo se
     * perde.
     */
    mostrarMapeamento() {
      this.$nextTick(() => {
        const bloco = this.$refs.mapeamento && this.$refs.mapeamento.$el;

        if (!bloco) {
          return;
        }

        bloco.scrollIntoView({ behavior: 'smooth', block: 'center' });
        bloco.classList.add('destacado');
        setTimeout(() => bloco.classList.remove('destacado'), 2400);
      });
    },

    escolherModo(modo) {
      this.modo = modo;
      gravarPreferencia(modo);
    },

    salvarEConectar(provedor) {
      this.conectarApos = provedor;

      this.salvar();
    },

    salvar() {
      this.erroGeral = '';
      this.errosCampos = {};

      /*
       * A recusa por licença é do servidor; aqui ela só chega antes, para o
       * usuário não preencher o guia inteiro e levar a negativa no fim.
       */
      if (!this.licenciado) {
        this.conectarApos = '';
        this.erroGeral = 'Esta instalação ainda não tem a licença deste módulo.';

        return;
      }

      if (!this.formulario.provedor) {
        this.erroGeral = 'Selecione o provedor da integração.';

        return;
      }

      if (this.exigeEscopo && !this.formulario.escopo) {
        this.erroGeral = 'Selecione a finalidade desta configuração.';

        return;
      }

      const dados = {
        catalogo_slug: this.conector.slug,
        provedor: this.formulario.provedor,
        nome: this.formulario.nome,
        escopo: this.formulario.escopo || null,

        /*
         * Criada pelo clique de conectar, nasce desligada: sem a conta
         * autorizada ela não tem o que fazer, e aparecer como ativa na lista
         * diria que já está valendo. Quem a liga é a autorização concluída —
         * cancelada no provedor, ela fica à vista, parada, para o usuário
         * retomar ou remover.
         *
         * Só na criação. Conectar outra conta numa integração que já está no
         * ar não é motivo para derrubá-la: o que ela faz hoje segue valendo
         * enquanto a autorização nova não volta.
         */
        ativo: this.conectarApos && !this.edicao ? false : this.formulario.ativo,

        /*
         * Gravar para conectar não é concluir: o servidor dispensa os campos
         * obrigatórios das etapas seguintes, que o usuário ainda nem viu. A
         * gravação que liga a integração continua exigindo tudo.
         */
        para_conectar: !!this.conectarApos,
        valores: this.formulario.valores,
        situacoes: this.formulario.situacoes,
        prioridade: this.formulario.prioridade,
      };

      this.salvando = true;

      const requisicao = this.edicao
        ? put(`/admin/integracoes/${this.integracao.id}`, dados)
        : post('/admin/integracoes', dados);

      requisicao
        .then((resposta) => {
          // Quem clicou em conectar pediu para conectar: anunciar que salvou
          // responde outra coisa, e some antes de o provedor abrir.
          if (!this.conectarApos) {
            this.$swal({
              title: resposta.message,
              icon: 'success',
              timer: 1800,
              showConfirmButton: false,
            });
          }

          /*
           * Recém-instalada, a integração fica na própria tela de configuração
           * em vez de voltar para a lista: nos conectores cujo caminho
           * principal é conectar a conta, o botão só existe depois de salvar —
           * mandar o usuário para a lista seria mandá-lo procurar de volta a
           * tela que ele acabou de deixar.
           */
          const criada = !this.edicao && resposta.data && resposta.data.id;

          /*
           * Terminado o guia, a tela completa: o guia serve para instalar, e
           * instalada a integração o que o usuário quer é ver o que ficou —
           * inclusive o mapeamento de campos e os eventos recebidos, que só
           * existem depois de haver instância. Sem isso ele terminava o guia e
           * não achava onde mexer no DE/PARA.
           *
           * Não grava preferência: é o desfecho deste fluxo, não uma escolha
           * sobre como ele prefere configurar.
           */
          if (criada && this.modoAssistente) {
            this.modo = 'completo';

            // A última etapa do guia prometeu que o mapeamento abriria ao
            // concluir: a tela abre nele, em vez de deixar procurar.
            this.irParaMapeamento = true;
          }

          if (criada) {
            this.$router.push({
              path: `/integracoes/${resposta.data.id}/configurar`,
              query: this.conectarApos ? { conectar: this.conectarApos } : {},
            });

            return;
          }

          /*
           * Editando pelo guia, salvar não tira o usuário da tela: ele está
           * mexendo numa parte e vai mexer na seguinte. Ir para a lista o
           * obrigaria a entrar de novo e reencontrar onde estava.
           */
          if (this.modoAssistente) {
            this.carregar();

            return;
          }

          this.$router.push('/integracoes');
        })
        .catch((erro) => this.tratarErro(erro))
        .finally(() => {
          this.salvando = false;

          /*
           * A intenção de conectar vale pela tentativa que a carregou. Ficando
           * ligada depois de um salvar recusado, o clique seguinte em Concluir
           * gravaria a integração desligada e sem os obrigatórios — o usuário
           * terminaria o guia achando que ligou, com ela fora do ar.
           */
          this.conectarApos = '';
        });
    },

    testar() {
      this.testando = true;
      this.resultadoTeste = null;

      post(`/admin/integracoes/${this.integracao.id}/testar-conexao`, {})
        .then((resposta) => {
          this.resultadoTeste = { sucesso: true, mensagem: resposta.message };
        })
        .catch((erro) => {
          this.resultadoTeste = { sucesso: false, mensagem: erro.message };
        })
        .finally(() => {
          this.testando = false;
          // O teste grava status e data na instância; recarrega para o painel
          // lateral refletir o resultado.
          this.recarregarEstadoDaInstancia();
        });
    },

    /*
     * A validação dos campos é do backend, contra o manifesto — repetir as
     * regras aqui criaria duas verdades. O que a tela faz é devolver cada
     * mensagem ao campo que a originou.
     */
    tratarErro(erro) {
      const resposta = erro.apiResponse || {};

      /*
       * A chave é própria, e não `errors`: esta já tem significado no helper
       * de requisição, que lê `errors[0].detail` e quebraria com um mapa.
       */
      const errosPorCampo = resposta.erros_por_campo;

      if (errosPorCampo && !Array.isArray(errosPorCampo)) {
        const mapeados = {};

        Object.keys(errosPorCampo).forEach((campo) => {
          const mensagem = errosPorCampo[campo];
          mapeados[campo] = Array.isArray(mensagem) ? mensagem[0] : mensagem;
        });

        this.errosCampos = mapeados;
        this.erroGeral = 'Revise os campos destacados.';

        return;
      }

      this.erroGeral = erro.message;
    },

    /*
     * Volta para a aba de onde se veio. Quem chegou pelo catálogo estranhava
     * cair na lista de configuradas, que é outra tela — a origem viaja na query
     * justamente para o botão não precisar adivinhar.
     */
    /*
     * O provedor devolve o navegador para esta mesma tela, com a sessão e o
     * `state` na query. É aqui que a autorização se conclui, e não no botão:
     * o componente que a iniciou não sobrevive ao redirecionamento.
     */
    concluirConexaoDoHub() {
      const { hub_session: sessao, state, hub_status: status, hub_error: motivo } = this.$route.query;

      if (!sessao || !state) {
        return;
      }

      // Limpa a query antes de qualquer coisa: um F5 depois disso tentaria
      // concluir a mesma sessão de novo, e o Hub só aceita uma vez.
      this.$router.replace({ query: this.queryLimpa() }).catch(() => {});

      if (status && status !== 'success') {
        this.erroGeral = this.mensagemDoRetorno(motivo);

        return;
      }

      this.carregando = true;

      post('/admin/integracoes/hub/concluir', { hub_session: sessao, state })
        .then((resposta) => {
          this.$swal({
            title: 'Conta conectada!',
            text: (resposta && resposta.message) || '',
            icon: 'success',
          });

          this.carregar();
        })
        .catch((erro) => {
          this.erroGeral = erro.message || 'Não foi possível concluir a conexão.';
          this.carregando = false;
        });
    },

    /**
     * A query sem os parâmetros que o Hub acrescentou.
     */
    queryLimpa() {
      const query = { ...this.$route.query };

      ['hub_session', 'hub_status', 'hub_error', 'state', 'conectar'].forEach((chave) => delete query[chave]);

      return query;
    },

    /*
     * Tira da URL a ordem de conectar assim que ela é cumprida.
     *
     * Enquanto ela ficar lá, o endereço guarda uma instrução em vez de um
     * lugar: voltar pelo navegador — inclusive voltando da tela do provedor —
     * reexecuta a conexão e manda o usuário de novo para fora do painel, sem
     * fim.
     */
    retomadaCumprida() {
      if (!this.$route.query.conectar) {
        return;
      }

      const query = { ...this.$route.query };
      delete query.conectar;

      this.$router.replace({ query }).catch(() => {});
    },

    /*
     * O motivo chega como código na URL, vindo do provedor pelo Hub. As frases
     * ficam aqui porque este caminho não passa pela API — o navegador volta
     * direto para esta tela — e o texto precisa estar pronto antes de qualquer
     * requisição. As mesmas frases existem em lang/pt-BR/hub.php, para o lado
     * de lá.
     */
    mensagemDoRetorno(motivo) {
      const motivos = {
        access_denied: 'A autorização foi cancelada antes de ser concluída.',
        provider_error: 'O provedor recusou a autorização. Confira se a conta tem permissão para conceder esse acesso.',
        session_expired: 'A autorização demorou demais e expirou. Comece de novo.',
      };

      return motivos[motivo] || 'A autorização não foi concluída.';
    },

    desconectarDoHub() {
      del(`/admin/integracoes/${this.integracao.id}/hub/conexao`)
        .then((resposta) => {
          this.$swal({
            title: 'Conta desconectada',
            text: (resposta && resposta.message) || '',
            icon: 'success',
          });
        })
        .catch((erro) => {
          this.$swal({ title: 'Ops!', text: erro.message, icon: 'error' });
        })
        /*
         * Recarrega também quando o DELETE falha: é a volta da conexão que
         * devolve os botões do bloco de conta ao usuário. Sem ela, um erro
         * aqui o deixava sem nada para clicar além de recarregar a página.
         */
        .finally(() => this.carregar());
    },

    voltar() {
      this.$router.push(this.telaDeOrigem.to);
    },
  },
  watch: {
    // Ir de uma integração para outra sem passar pela lista reaproveitaria o
    // componente com os dados antigos.
    $route: 'carregar',
  },
  beforeMount() {
    this.carregar();
    this.concluirConexaoDoHub();
  },
};
</script>

<style scoped>
.breadcrumb-integracao {
  background: transparent;
  padding: 0 0 12px;
  margin-bottom: 0;
  font-size: 13px;
}

.cabecalho {
  margin-bottom: 18px;
}

.cabecalho {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

/*
 * O conteúdo vive dentro da aba, como nas demais telas do painel (Cadastro de
 * filiais, Parâmetros): é a aba que nomeia a tela, e o conteúdo fica no card
 * colado nela. Este cabeçalho interno identifica o conector sem competir com
 * ela.
 */
.cabecalho__identificacao {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cabecalho__nome {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1d2939;
}

.cabecalho__descricao {
  margin: 2px 0 0;
  font-size: 13px;
  color: #667085;
}

.selo-parceiro {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 20px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  color: #14727c;
  font-size: 11px;
  font-weight: 700;
  vertical-align: middle;
}

.cabecalho__site {
  margin-left: 8px;
  color: #2298a3;
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
}

.cabecalho__site:hover {
  text-decoration: underline;
}

.selo-beta {
  padding: 2px 9px;
  border-radius: 12px;
  background: #fef4e6;
  color: #b25e09;
  font-size: 11px;
  font-weight: 600;
}

.finalidades {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.finalidades__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 20px;
  background: #fff;
  color: #667085;
  font-size: 12px;
  cursor: pointer;
}

.finalidades__item:hover {
  border-color: #b9b7d1;
}

.finalidades__item--atual {
  border-color: #3a3e81;
  background: #eceaf3;
  color: #3a3e81;
  font-weight: 600;
}

.finalidades__item--vazia {
  border-style: dashed;
  color: #98a2b3;
}

.finalidades__pendente {
  font-size: 11px;
  color: #98a2b3;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 20px;
  align-items: start;
}

/*
 * No guia não há coluna lateral — o que ela dizia virou o texto de cada etapa.
 * Sem isto a grade continuaria reservando o espaço dela, e o formulário ficaria
 * espremido em dois terços da tela com um vazio ao lado.
 */
.layout--unico {
  grid-template-columns: minmax(0, 1fr);
}

/* Abaixo de 992px a coluna lateral desce para baixo do formulário. */
@media (max-width: 991px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 4px;
}

.obrigatorio {
  color: #c0392b;
}

.campo-ajuda {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.provedores {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.provedor-card {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.provedor-card:hover {
  border-color: #b9b7d1;
}

.provedor-card--ativo {
  border-color: #3a3e81;
  background: #eceaf3;
}

.provedor-card__nome {
  font-size: 14px;
  font-weight: 600;
  color: #1d2939;
}

.provedor-card__descricao {
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.situacao-linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
}

.situacao-linha .rotulo {
  color: #667085;
}

.situacao-linha .valor {
  color: #344054;
  font-weight: 500;
}

.situacao-erro {
  margin: 8px 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fdeceb;
  color: #b42318;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-word;
}

.botao-largo {
  width: 100%;
  margin-top: 10px;
}

.sem-teste {
  margin: 10px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}


.rodape-acoes__corpo--sem-licenca {
  align-items: center;
  color: #667085;
  display: flex;
  font-size: 13px;
  gap: 10px;
  justify-content: flex-start;
  line-height: 1.5;
}

.rodape-acoes__corpo--sem-licenca strong {
  color: #3a3e81;
}

/*
 * Destaque momentâneo do bloco a que a tela acabou de levar: sem ele, a página
 * só aparece rolada e a ligação com o que o usuário acabou de fazer se perde.
 */
.destacado {
  animation: destacar 2.4s ease;
}

@keyframes destacar {
  0%, 60% {
    box-shadow: 0 0 0 2px #3a3e81;
  }

  100% {
    box-shadow: 0 0 0 2px rgba(58, 62, 129, 0);
  }
}

.alternar-modo {
  background: none;
  border: 0;
  color: #667085;
  cursor: pointer;
  display: block;
  font-size: 12px;
  margin: 14px auto 0;
  padding: 4px;
  text-decoration: underline;
}

.alternar-modo:hover {
  color: #3a3e81;
}

.rodape-acoes__corpo {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.rodape-acoes__aviso {
  margin-right: auto;
  font-size: 12px;
  color: #667085;
}
</style>
